import { ReactNode, useEffect, useState } from 'react';
import { ConfigProvider, App as AntApp, theme as antdTheme } from 'antd';
import enUS from 'antd/locale/en_US';
import { theme as appTheme } from '@/config/theme';
import { ThemeContext, ThemeMode } from './ThemeContext';

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Get initial theme from localStorage or system preference
  const getInitialTheme = (): ThemeMode => {
    const savedTheme = localStorage.getItem('theme') as ThemeMode;
    if (savedTheme) return savedTheme;

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  const [currentTheme, setCurrentTheme] = useState<ThemeMode>(getInitialTheme);

  // Update document class and localStorage when theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (currentTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', currentTheme);
  }, [currentTheme]);

  const toggleTheme = () => {
    setCurrentTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setTheme = (theme: ThemeMode) => {
    setCurrentTheme(theme);
  };

  // Ant Design theme configuration with dynamic dark/light mode
  const antdThemeConfig = {
    algorithm: currentTheme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: appTheme.antd.colorPrimary,
      colorSuccess: appTheme.antd.colorSuccess,
      colorWarning: appTheme.antd.colorWarning,
      colorError: appTheme.antd.colorError,
      colorInfo: appTheme.antd.colorInfo,
      borderRadius: appTheme.antd.borderRadius,
      controlHeight: appTheme.antd.controlHeight,
      fontSize: appTheme.antd.fontSize,
      fontFamily: appTheme.antd.fontFamily,

      // Dark mode specific overrides
      ...(currentTheme === 'dark' && {
        colorBgContainer: appTheme.dark.antd.colorBgContainer,
        colorBgElevated: appTheme.dark.antd.colorBgElevated,
        colorBgLayout: appTheme.dark.antd.colorBgLayout,
        colorBorder: appTheme.dark.antd.colorBorder,
        colorText: appTheme.dark.antd.colorText,
        colorTextSecondary: appTheme.dark.antd.colorTextSecondary,
        colorTextTertiary: appTheme.dark.antd.colorTextTertiary,
      }),
    },
    components: {
      Button: {
        controlHeight: 48,
        fontSize: 14,
        borderRadius: 16,
      },
      Input: {
        controlHeight: 48,
        borderRadius: 16,
      },
      Select: {
        controlHeight: 48,
        borderRadius: 16,
      },
      Card: {
        borderRadius: 28,
      },
      Layout: {
        ...(currentTheme === 'dark' && {
          headerBg: appTheme.dark.colors.surface,
          bodyBg: appTheme.dark.colors.background,
          siderBg: appTheme.dark.colors.surface,
        }),
      },
      Menu: {
        ...(currentTheme === 'dark' && {
          itemBg: 'transparent',
          itemSelectedBg: '#f97316',
          itemHoverBg: appTheme.dark.colors.surfaceHover,
          itemColor: appTheme.dark.colors.text.secondary,
          itemSelectedColor: '#ffffff',
          itemHoverColor: appTheme.antd.colorPrimary,
        }),
      },
    },
  };

  return (
    <ThemeContext.Provider
      value={{ theme: currentTheme, isDark: currentTheme === 'dark', toggleTheme, setTheme }}
    >
      <ConfigProvider theme={antdThemeConfig} locale={enUS}>
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
